import { Component, OnDestroy, OnInit, ViewChild, } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IonContent } from '@ionic/angular';

import {
  catchError,
  concatMap,
  defer,
  filter,
  from,
  map,
  Observable,
  of,
  Subscription,
  take,
  throwError,
} from 'rxjs';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { datadogRum } from '@datadog/browser-rum';
import { Capacitor } from '@capacitor/core';
import { CapacitorEventBird } from 'capacitor-event-bird';

import { LocalStorageService } from '@tablesready/frontend-storage';
import { FaviconService } from '@tablesready/common-ui';
import { ThemeSwitchService } from '@tablesready/frontend-theme-switch';
import {
  AuthService,
  NEW_ACCOUNT_CREATED_AT_STORAGE_KEY,
  rumSessionSampleRate,
} from '@tablesready/frontend-auth';
import { FrontendResumeBackgroundService } from '@tablesready/frontend-resume-background';
import { FrontendKeyboardService } from '@tablesready/frontend-keyboard';
import { LoginService } from './auth/services';
import { FirebaseService, PushNotificationsService, FcmTokenApiService } from './services';
import { NavigationService } from '@tablesready/frontend-navigation';
import { ConsentService } from './consent/consent.service';
import { logoutFcmCleanup$ } from './services/logout-fcm-cleanup';
import { GUEST_LINK_HELP_PATH } from './guest-link/guest-link.guard';

/**
 * Routes rendered outside AuthenticatedLayoutComponent (see app-routing.module).
 * These screens are light-only by design — their markup assumes a light card on
 * the dark brand background — so the dark palette must not reach them.
 */
const UNAUTHENTICATED_ROUTES = [
  '/login',
  '/invite',
  '/verify',
  '/restore-password',
  GUEST_LINK_HELP_PATH,
];

@Component({
  selector: 'tablesready-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(IonContent) ionContentRef?: IonContent;
  timezone = '';

  private readonly subscriptions = new Subscription();
  private themeScope: 'unauthenticated' | 'app' | null = null;

  constructor(
    private readonly router: Router,
    private readonly theme: ThemeSwitchService,
    private readonly faviconService: FaviconService,
    private readonly angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private readonly resumeService: FrontendResumeBackgroundService,
    private readonly keyboardService: FrontendKeyboardService,
    private readonly route: ActivatedRoute,
    private readonly routerEvents: NavigationService, // do not remove it
    private readonly loginService: LoginService,
    private readonly firebaseService: FirebaseService,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly fcmTokenApiService: FcmTokenApiService,
    private readonly storage: LocalStorageService,
    private readonly consentService: ConsentService,
    readonly authService: AuthService,
  ) {
    if (!Capacitor.isNativePlatform()) {
      this.firebaseService.initializeApp();
      void this.pushNotificationsService.init();
    }

    this.angulartics2GoogleTagManager.startTracking();
    this.keyboardService.init();

    let applicationId = '';
    let env = 'develop';
    let clientToken = '';
    let version = '';

    try {
      applicationId = process.env.DD_RUM_APPID;
      env = process.env.DD_RUM_ENV;
      clientToken = process.env.DD_RUM_CLIENTTOKEN;
      // Injected at build time from package.json (see extra-webpack.config.ts). Must
      // stay in step with the --release-version the deploy workflows upload source
      // maps under, or symbolication breaks. Left unset (below) rather than faked
      // if the build did not define it: a made-up version can never match an upload.
      version = process.env.APP_VERSION;
    } catch (e) {
      console.log({ e });
    }

    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service: 'tablesready',
      version: version || undefined,
      env,
      sessionSampleRate: rumSessionSampleRate(
        this.authService.isAuthenticated(),
        this.storage.get<number>(NEW_ACCOUNT_CREATED_AT_STORAGE_KEY),
        Date.now(),
      ),
      sessionReplaySampleRate: 25,
      // capacitor:// pages can't keep cookies, and without a session store the SDK sends
      // nothing at all
      sessionPersistence: Capacitor.isNativePlatform() ? 'local-storage' : 'cookie',
      trackUserInteractions: true,
      defaultPrivacyLevel: 'mask-user-input',
      // The Terms cover recordings of signed-in use only: AuthenticatedLayoutComponent
      // starts replay and stops it on destroy, so the login, signup, invite, verify and
      // password pages are never recorded
      startSessionReplayRecordingManually: true,
      allowedTracingUrls: [
        'https://api.tablesready.com',
        'https://host.tablesready.com',
        'https://tablesready-production.herokuapp.com',
        'https://tablesready-staging.herokuapp.com',
      ],
    });

    const rumContext = datadogRum.getInternalContext();

    if ((window as any).Beacon && rumContext) {
      (window as any).Beacon('identify', { sessionURL: rumContext.session_id });
    }

    this.route.queryParams
      .pipe(
        filter(params => Object.values(params).length > 0),
        take(1),
        concatMap((params) => {
          if (!params['authtoken']) {
            return of(null);
          }

          return this.loginService.loginByToken(params.authtoken, params.locationId);
        }),
        catchError(err => throwError(err)),
      )
      .subscribe((tokens) => {
        if (!tokens) {
          return;
        }

        setTimeout(() => {
          void this.router.navigate(['/settings/location/billing']);
        });
      });
  }

  ngOnInit(): void {
    // Decided from location.pathname rather than router.url: at this point the
    // first navigation hasn't resolved yet, and reading router.url would briefly
    // apply the dark palette on a deep-linked login/invite page.
    this.applyThemeForUrl(window.location.pathname);
    const themeSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(({ urlAfterRedirects }) => this.applyThemeForUrl(urlAfterRedirects));
    this.subscriptions.add(themeSub);

    this.faviconService.generate();
    this.resumeService.init();

    // Consent: authenticated users are auto opted-in to analytics per the Terms;
    // direct-to-app visitors with no prior choice get the deny-by-default banner.
    if (this.authService.isAuthenticated()) {
      this.consentService.grantAnalyticsConsent();
    } else {
      this.consentService.init();
    }

    const logoutSub = this.authService.onLogOut.subscribe(() => {
      // Before the redirect, so the login page never gets a replay or the old user
      datadogRum.stopSessionReplayRecording();
      datadogRum.clearUser();
      // The next login gets a session of its own, so a replay forced for a new account
      // can't carry over to whoever signs in next on this device
      datadogRum.stopSession();
      this.storage.remove(NEW_ACCOUNT_CREATED_AT_STORAGE_KEY);
      // Redirect first. The FCM cleanup is best-effort, bounded and fire-and-forget: the
      // redirect must never wait on it (a hung request used to strand a running app after
      // session expiry), and it can never error this subscription.
      void this.router.navigate(['/login']);
      const cleanupSub = logoutFcmCleanup$(
        defer(() => this.getFirebaseDeviceId()),
        deviceId => this.fcmTokenApiService.deleteFcmTokenForDeviceId(deviceId),
      ).subscribe();
      this.subscriptions.add(cleanupSub);
    });
    this.subscriptions.add(logoutSub);
  }

  private getFirebaseDeviceId(): Observable<string | undefined> {
    if (Capacitor.isNativePlatform()) {
      return from(CapacitorEventBird.getDeviceId()).pipe(map(({ value }) => value));
    }

    return of(this.storage.get<string>('firebaseDeviceId'));
  }

  reload(): void {
    location.reload();
  }

  /**
   * Force light on the unauthenticated screens, restore the user's saved theme
   * inside the app. Tracked so repeated navigations within the same area don't
   * re-apply (and re-emit) the theme on every route change.
   */
  private applyThemeForUrl(url: string): void {
    const path = url.split(/[?#]/)[0];
    const isUnauthenticated = UNAUTHENTICATED_ROUTES
      .some(route => path === route || path.startsWith(`${route}/`));
    const scope = isUnauthenticated ? 'unauthenticated' : 'app';

    if (scope === this.themeScope) {
      return;
    }
    this.themeScope = scope;

    if (isUnauthenticated) {
      this.theme.forceLightTheme();
      return;
    }
    this.theme.initializeTheme();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
